import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { getAllRoles } from "../../../../../api/api";
import {
  assignPortalJobTypesToRole,
  getAllPortalJobTypes,
} from "../../../../../api/portalJobTypesApi";
import { selectStyles } from "../../../../../utils/selectStyles";
import "../CreateJobType/CreateJobType.css";
import "./AssignJobType.css";

const ASSIGN_MODES = {
  DIRECT: "direct",
  COPY: "copy",
};

const BINDING_TYPE_LABELS = {
  Проект: "По проекту",
  "-1": "Без проекта",
  ИК: "ИК",
};

const uniqueById = (items) => {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).filter((item) => {
    if (item?.id == null || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const formatJobTypeLabel = (item) => {
  const name = item.name || `Вид работ ${item.id}`;
  const binding =
    BINDING_TYPE_LABELS[item.type_of_fin_job_type] ||
    item.type_of_fin_job_type ||
    "";
  const parts = [name];
  if (binding) parts.push(binding);
  if (item.role_name) parts.push(item.role_name);
  return `${parts.join(" · ")} (id: ${item.id})`;
};

const mapJobTypesToOptions = (items) =>
  uniqueById(items)
    .filter((item) => item.is_active !== false)
    .map((item) => ({
      value: item.id,
      label: formatJobTypeLabel(item),
      raw: item,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "ru"));

function AssignJobType() {
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState("");

  const [allJobTypes, setAllJobTypes] = useState([]);
  const [allJobTypesLoading, setAllJobTypesLoading] = useState(true);
  const [allJobTypesError, setAllJobTypesError] = useState("");

  const [targetRole, setTargetRole] = useState(null);
  const [mode, setMode] = useState(ASSIGN_MODES.DIRECT);
  const [sourceRole, setSourceRole] = useState(null);
  const [copyAll, setCopyAll] = useState(true);
  const [selectedJobTypes, setSelectedJobTypes] = useState([]);

  const [assignedJobTypes, setAssignedJobTypes] = useState([]);
  const [assignedLoading, setAssignedLoading] = useState(false);
  const [assignedError, setAssignedError] = useState("");

  const [sourceJobTypes, setSourceJobTypes] = useState([]);
  const [sourceJobTypesLoading, setSourceJobTypesLoading] = useState(false);
  const [sourceJobTypesError, setSourceJobTypesError] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const assignedIds = useMemo(
    () => new Set(assignedJobTypes.map((item) => item.id)),
    [assignedJobTypes],
  );

  const sourceRoleOptions = useMemo(
    () => roles.filter((role) => role.value !== targetRole?.value),
    [roles, targetRole],
  );

  const directJobTypeOptions = useMemo(
    () => allJobTypes.filter((option) => !assignedIds.has(option.value)),
    [allJobTypes, assignedIds],
  );

  const sourceJobTypeOptions = useMemo(
    () => sourceJobTypes.filter((option) => !assignedIds.has(option.value)),
    [sourceJobTypes, assignedIds],
  );

  const jobTypeOptions =
    mode === ASSIGN_MODES.COPY ? sourceJobTypeOptions : directJobTypeOptions;

  useEffect(() => {
    let cancelled = false;

    const loadRoles = async () => {
      setRolesLoading(true);
      setRolesError("");
      try {
        const data = await getAllRoles(null, true);
        if (cancelled) return;
        const options = (Array.isArray(data) ? data : [])
          .filter((role) => role?.id != null && role?.name)
          .map((role) => ({
            value: role.id,
            label: role.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "ru"));
        setRoles(options);
      } catch (err) {
        if (!cancelled) {
          setRolesError(err.message || "Не удалось загрузить должности");
        }
      } finally {
        if (!cancelled) {
          setRolesLoading(false);
        }
      }
    };

    loadRoles();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadAllJobTypes = async () => {
      setAllJobTypesLoading(true);
      setAllJobTypesError("");
      try {
        const list = await getAllPortalJobTypes({ is_active: true });
        if (cancelled) return;
        setAllJobTypes(mapJobTypesToOptions(list));
      } catch (err) {
        if (!cancelled) {
          setAllJobTypesError(
            err.message || "Не удалось загрузить виды работ",
          );
        }
      } finally {
        if (!cancelled) {
          setAllJobTypesLoading(false);
        }
      }
    };

    loadAllJobTypes();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadAssignedJobTypes = useCallback(async (roleId) => {
    if (roleId == null) {
      setAssignedJobTypes([]);
      setAssignedError("");
      return;
    }

    setAssignedLoading(true);
    setAssignedError("");
    try {
      const list = await getAllPortalJobTypes({ role_id: roleId });
      setAssignedJobTypes(uniqueById(list));
    } catch (err) {
      setAssignedJobTypes([]);
      setAssignedError(
        err.message || "Не удалось загрузить виды работ должности",
      );
    } finally {
      setAssignedLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssignedJobTypes(targetRole?.value);
  }, [targetRole, loadAssignedJobTypes]);

  useEffect(() => {
    setSelectedJobTypes((prev) => {
      const next = prev.filter((option) => !assignedIds.has(option.value));
      return next.length === prev.length ? prev : next;
    });
  }, [assignedIds]);

  useEffect(() => {
    if (mode !== ASSIGN_MODES.COPY || sourceRole?.value == null) {
      setSourceJobTypes([]);
      setSourceJobTypesError("");
      setSourceJobTypesLoading(false);
      return;
    }

    let cancelled = false;

    const loadSourceJobTypes = async () => {
      setSourceJobTypesLoading(true);
      setSourceJobTypesError("");
      try {
        const list = await getAllPortalJobTypes({
          role_id: sourceRole.value,
          is_active: true,
        });
        if (cancelled) return;
        setSourceJobTypes(mapJobTypesToOptions(list));
      } catch (err) {
        if (!cancelled) {
          setSourceJobTypes([]);
          setSourceJobTypesError(
            err.message || "Не удалось загрузить виды работ исходной должности",
          );
        }
      } finally {
        if (!cancelled) {
          setSourceJobTypesLoading(false);
        }
      }
    };

    loadSourceJobTypes();
    return () => {
      cancelled = true;
    };
  }, [mode, sourceRole]);

  const handleTargetRoleChange = (option) => {
    setTargetRole(option);
    setSubmitError("");
    setSuccessMessage("");
    if (sourceRole && option?.value === sourceRole.value) {
      setSourceRole(null);
      setSelectedJobTypes([]);
    }
  };

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    setSourceRole(null);
    setCopyAll(nextMode === ASSIGN_MODES.COPY);
    setSelectedJobTypes([]);
    setSubmitError("");
    setSuccessMessage("");
    setSourceJobTypes([]);
    setSourceJobTypesError("");
  };

  const handleSourceRoleChange = (option) => {
    setSourceRole(option);
    setSelectedJobTypes([]);
    setSubmitError("");
    setSuccessMessage("");
  };

  const isFormValid = () => {
    if (!targetRole?.value) return false;
    if (mode === ASSIGN_MODES.COPY) {
      if (!sourceRole?.value) return false;
      if (copyAll) return sourceJobTypeOptions.length > 0;
      return selectedJobTypes.length > 0;
    }
    return selectedJobTypes.length > 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSuccessMessage("");

    if (!isFormValid()) {
      setSubmitError("Заполните обязательные поля для привязки.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        role_id: Number(targetRole.value),
      };

      if (mode === ASSIGN_MODES.COPY) {
        payload.source_role_id = Number(sourceRole.value);
        if (!copyAll) {
          payload.portal_job_type_ids = selectedJobTypes.map((item) =>
            Number(item.value),
          );
        }
      } else {
        payload.portal_job_type_ids = selectedJobTypes.map((item) =>
          Number(item.value),
        );
      }

      const result = await assignPortalJobTypesToRole(payload);
      await loadAssignedJobTypes(targetRole.value);

      const fallbackMessage =
        mode === ASSIGN_MODES.COPY
          ? copyAll
            ? "Виды работ скопированы на выбранную должность"
            : "Выбранные виды работ скопированы на должность"
          : "Выбранные виды работ привязаны к должности";

      setSuccessMessage(
        typeof result === "string" && result.trim() ? result : fallbackMessage,
      );
      setSelectedJobTypes([]);
      setTimeout(() => setSuccessMessage(""), 4000);
    } catch (err) {
      setSubmitError(
        err.message || "Не удалось привязать виды работ к должности",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const showJobTypeSelect =
    mode === ASSIGN_MODES.DIRECT || (mode === ASSIGN_MODES.COPY && !copyAll);

  const jobTypesLoading =
    mode === ASSIGN_MODES.COPY ? sourceJobTypesLoading : allJobTypesLoading;
  const jobTypesError =
    mode === ASSIGN_MODES.COPY ? sourceJobTypesError : allJobTypesError;

  const jobTypesPlaceholder = () => {
    if (mode === ASSIGN_MODES.COPY && !sourceRole) {
      return "Сначала выберите должность-источник";
    }
    if (jobTypesLoading) return "Загрузка...";
    return "Выберите один или несколько видов работ";
  };

  return (
    <div className="create-job-type assign-job-type">
      {successMessage && (
        <div className="create-job-type__success" role="status">
          {successMessage}
        </div>
      )}

      <section className="create-job-type__section">
        <h2 className="create-job-type__section-title">
          Привязка видов работ к должности
        </h2>
        <p className="create-job-type__section-desc">
          Выберите должность и уже существующие виды работ — новые записи не
          создаются. Можно скопировать виды работ с другой должности. Уже
          существующие связи будут пропущены.
        </p>

        <form className="create-job-type__form" onSubmit={handleSubmit}>
          <ul className="create-job-type__fields">
            <li className="create-job-type__field">
              <label className="create-job-type__label">Должность*</label>
              <Select
                options={roles}
                value={targetRole}
                onChange={handleTargetRoleChange}
                styles={selectStyles}
                placeholder={
                  rolesLoading ? "Загрузка..." : "Выберите должность"
                }
                isLoading={rolesLoading}
                isDisabled={rolesLoading || !!rolesError || isSubmitting}
                noOptionsMessage={() => "Должности не найдены"}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              {rolesError && (
                <span className="create-job-type__error">{rolesError}</span>
              )}
            </li>

            {targetRole && (
              <li className="create-job-type__field">
                <div className="assign-job-type__assigned-header">
                  <span className="create-job-type__label">
                    Уже привязаны к должности
                  </span>
                  <button
                    type="button"
                    className="assign-job-type__reload"
                    onClick={() => loadAssignedJobTypes(targetRole.value)}
                    disabled={assignedLoading || isSubmitting}
                  >
                    {assignedLoading ? "Загрузка..." : "Обновить"}
                  </button>
                </div>
                {assignedError && (
                  <span className="create-job-type__error">{assignedError}</span>
                )}
                {!assignedLoading &&
                  !assignedError &&
                  assignedJobTypes.length === 0 && (
                    <p className="create-job-type__hint">
                      У этой должности пока нет привязанных видов работ.
                    </p>
                  )}
                {assignedJobTypes.length > 0 && (
                  <ul className="assign-job-type__assigned">
                    {assignedJobTypes.map((item) => (
                      <li key={item.id} className="assign-job-type__assigned-item">
                        {item.name || `id ${item.id}`}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}

            <li className="create-job-type__field">
              <span className="create-job-type__label">Способ привязки</span>
              <div className="assign-job-type__modes" role="radiogroup">
                <label className="create-job-type__checkbox-label">
                  <input
                    type="radio"
                    name="assign-job-type-mode"
                    checked={mode === ASSIGN_MODES.DIRECT}
                    onChange={() => handleModeChange(ASSIGN_MODES.DIRECT)}
                    disabled={isSubmitting}
                  />
                  <span>Выбрать существующие виды работ</span>
                </label>
                <label className="create-job-type__checkbox-label">
                  <input
                    type="radio"
                    name="assign-job-type-mode"
                    checked={mode === ASSIGN_MODES.COPY}
                    onChange={() => handleModeChange(ASSIGN_MODES.COPY)}
                    disabled={isSubmitting}
                  />
                  <span>Скопировать с другой должности</span>
                </label>
              </div>
            </li>

            {mode === ASSIGN_MODES.COPY && (
              <>
                <li className="create-job-type__field">
                  <label className="create-job-type__label">
                    Должность-источник*
                  </label>
                  <Select
                    options={sourceRoleOptions}
                    value={sourceRole}
                    onChange={handleSourceRoleChange}
                    styles={selectStyles}
                    placeholder={
                      !targetRole
                        ? "Сначала выберите должность"
                        : "Выберите должность, с которой копировать"
                    }
                    isDisabled={
                      !targetRole || rolesLoading || !!rolesError || isSubmitting
                    }
                    noOptionsMessage={() => "Должности не найдены"}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </li>

                <li className="create-job-type__field create-job-type__field--checkbox">
                  <label className="create-job-type__checkbox-label">
                    <input
                      type="checkbox"
                      checked={copyAll}
                      onChange={(e) => {
                        setCopyAll(e.target.checked);
                        if (e.target.checked) {
                          setSelectedJobTypes([]);
                        }
                        setSubmitError("");
                      }}
                      disabled={!sourceRole || isSubmitting}
                    />
                    <span>Скопировать все виды работ с этой должности</span>
                  </label>
                </li>
              </>
            )}

            {showJobTypeSelect && (
              <li className="create-job-type__field">
                <label className="create-job-type__label">Виды работ*</label>
                <p className="create-job-type__hint">
                  Уже привязанные к выбранной должности виды работ скрыты из
                  списка.
                </p>
                <Select
                  isMulti
                  options={jobTypeOptions}
                  value={selectedJobTypes}
                  onChange={(value) => {
                    setSelectedJobTypes(value || []);
                    setSubmitError("");
                  }}
                  styles={selectStyles}
                  placeholder={jobTypesPlaceholder()}
                  isLoading={jobTypesLoading}
                  isDisabled={
                    isSubmitting ||
                    jobTypesLoading ||
                    !!jobTypesError ||
                    (mode === ASSIGN_MODES.COPY && !sourceRole)
                  }
                  noOptionsMessage={() =>
                    mode === ASSIGN_MODES.COPY && sourceRole
                      ? "Нет видов работ для копирования"
                      : "Виды работ не найдены"
                  }
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {jobTypesError && (
                  <span className="create-job-type__error">{jobTypesError}</span>
                )}
              </li>
            )}

            {mode === ASSIGN_MODES.COPY &&
              copyAll &&
              sourceRole &&
              !sourceJobTypesLoading &&
              !sourceJobTypesError && (
                <li className="create-job-type__field">
                  <span className="create-job-type__label">
                    Будут скопированы
                  </span>
                  {sourceJobTypes.length === 0 ? (
                    <p className="create-job-type__hint">
                      У должности-источника нет видов работ.
                    </p>
                  ) : sourceJobTypeOptions.length === 0 ? (
                    <p className="create-job-type__hint">
                      Все виды работ этой должности уже привязаны к «
                      {targetRole?.label}».
                    </p>
                  ) : (
                    <p className="create-job-type__hint">
                      {sourceJobTypes.length}{" "}
                      {sourceJobTypes.length === 1
                        ? "вид работ"
                        : sourceJobTypes.length < 5
                          ? "вида работ"
                          : "видов работ"}
                      {sourceJobTypeOptions.length !== sourceJobTypes.length
                        ? `. Часть уже привязана к «${targetRole?.label}» и будет пропущена.`
                        : "."}
                    </p>
                  )}
                </li>
              )}
          </ul>

          {submitError && (
            <p className="create-job-type__error create-job-type__error--submit">
              {submitError}
            </p>
          )}

          <button
            type="submit"
            className="create-job-type__submit"
            disabled={isSubmitting || !isFormValid()}
          >
            {isSubmitting
              ? "Сохранение..."
              : mode === ASSIGN_MODES.COPY
                ? "Скопировать на должность"
                : "Привязать к должности"}
          </button>
        </form>
      </section>
    </div>
  );
}

export default AssignJobType;
